import { Mic as MicIcon, Stop as StopIcon } from "@mui/icons-material";
import { Alert, Box, Button, CircularProgress, Stack, TextField, Typography } from "@mui/material";
import { useCallback } from "react";

import { successGradientButtonSx } from "./AddEventTimesModal.theme";
import { STRINGS } from "./AddEventTimesModal.utils";
import { useEventScheduleAssistant } from "./useEventScheduleAssistant";
import { useVoiceRecorder } from "./useVoiceRecorder";

interface EventScheduleVoicePanelProps {
  description: string;
  eventId: string;
  isApplying?: boolean;
  onApplyDescription: (description: string) => Promise<void>;
  onDescriptionChange: (description: string) => void;
  warnings?: string[] | null;
}

export const EventScheduleVoicePanel = ({
  description,
  eventId,
  isApplying = false,
  onApplyDescription,
  onDescriptionChange,
  warnings,
}: EventScheduleVoicePanelProps) => {
  const { error, isParsing, isTranscribing, setError, transcribeAudio } = useEventScheduleAssistant(eventId);
  const { error: recorderError, isRecording, isSupported, toggleRecording } = useVoiceRecorder();

  const isBusy = isParsing || isTranscribing || isApplying;
  const canApply = description.trim().length > 0 && !isBusy;

  const handleMicClick = useCallback(async () => {
    if (isBusy) {
      return;
    }

    if (isRecording) {
      const audio = await toggleRecording();
      if (!audio) {
        return;
      }

      const transcript = await transcribeAudio(audio);
      if (transcript) {
        onDescriptionChange(description.trim() ? `${description.trim()} ${transcript}` : transcript);
      }

      return;
    }

    setError(null);
    await toggleRecording();
  }, [description, isBusy, isRecording, onDescriptionChange, setError, toggleRecording, transcribeAudio]);

  const handleApply = useCallback(async () => {
    if (!canApply) {
      return;
    }

    await onApplyDescription(description.trim());
  }, [canApply, description, onApplyDescription]);

  return (
    <Stack spacing={2}>
      <Typography color="text.secondary" variant="body2">
        {STRINGS.tVoiceDescriptionHint}
      </Typography>

      <TextField
        disabled={isBusy}
        fullWidth
        label={STRINGS.tVoiceDescriptionLabel}
        minRows={4}
        multiline
        onChange={(event) => onDescriptionChange(event.target.value)}
        placeholder={STRINGS.tVoiceDescriptionPlaceholder}
        value={description}
      />

      <Stack alignItems={{ sm: "center", xs: "stretch" }} direction={{ sm: "row", xs: "column" }} spacing={1.5}>
        <Button
          color={isRecording ? "error" : "primary"}
          disabled={!isSupported || isBusy}
          onClick={() => void handleMicClick()}
          startIcon={
            isTranscribing ? <CircularProgress aria-hidden size={18} /> : isRecording ? <StopIcon /> : <MicIcon />
          }
          variant={isRecording ? "contained" : "outlined"}
        >
          {isTranscribing
            ? STRINGS.tVoiceTranscribing
            : isRecording
              ? STRINGS.tVoiceStopRecording
              : STRINGS.tVoiceStartRecording}
        </Button>

        <Box sx={{ flex: 1 }} />

        <Button
          disabled={!canApply}
          onClick={() => void handleApply()}
          startIcon={isParsing || isApplying ? <CircularProgress aria-hidden color="inherit" size={18} /> : undefined}
          sx={successGradientButtonSx}
          variant="contained"
        >
          {STRINGS.tVoiceApplyDescription}
        </Button>
      </Stack>

      {recorderError ? (
        <Alert severity="error" variant="outlined">
          {recorderError}
        </Alert>
      ) : null}

      {error ? (
        <Alert severity="error" variant="outlined">
          {error}
        </Alert>
      ) : null}

      {warnings?.map((warning) => (
        <Alert key={warning} severity="warning" variant="outlined">
          {warning}
        </Alert>
      ))}
    </Stack>
  );
};
